<template>
  <Loading v-if="componentLoading" :style="style" />
  <view v-else class="data-row" :flex="flex" :style="style">
    <view class="data-item" v-for="(item, index) of fixList" :key="index" :style="itemStyle">
      {{ item }}
    </view>
    <template v-if="fliperTextRef.length > 0 && rollList.length > 0">
      <TextFliper
        v-for="(item, index) of fliperTextRef"
        :key="index"
        :style="itemStyle"
        :time="duration"
        :front-text="item.frontText"
        :back-text="item.backText"
        :flipType="flipType"
      />
    </template>
  </view>
</template>

<script lang="ts" setup>
import { ref, computed, onUnmounted } from "vue"
import Loading from "@p-v/packages/Loading.vue"
import TextFliper from "@p-v/packages/TextFliper/index.vue"
import { cloneDeep } from "lodash-es"

interface DataRowProps {
  style?: { [key: string]: string | number }
  itemStyle?: { [key: string]: string | number }
  flex?: string
  showNums?: number
  fixNums?: number
  duration?: number
  flipType: string
}

const props = withDefaults(defineProps<DataRowProps>(), {
  style: () => ({}),
  itemStyle: () => ({
    width: "80px",
    height: "40px",
    fontSize: "14px",
  }),
  flex: () => "dir:left main:center cross:center",
  showNums: () => 2,
  fixNums: () => 0,
  duration: () => 5000,
  flipType: () => "down",
})

const itemStyle = computed(() => {
  const style = Object.assign(
    {
      width: "80px",
      height: "40px",
      fontSize: "14px",
      background: "#fff",
      color: "#000",
    },
    props.itemStyle,
  )
  let lineHeight = style.lineHeight
  if (!lineHeight) {
    lineHeight = style.height
  }
  return {
    ...style,
    lineHeight,
  }
})

const componentLoading = ref<boolean>(true)

const fixList = ref<any[]>([])

const rollList = ref<any[]>([])

const fliperTextRef = ref<{ frontText: any | undefined; backText: any | undefined }[]>([])

const loading = () => {
  componentLoading.value = true
}

let roundTimer: any = null
let rollStartIndex = 0
let frontRoll: any[] = []
let endRoll: any[] = []

const change = () => {
  if (endRoll.length > 0) {
    for (let i = 0; i < endRoll.length; i++) {
      frontRoll[i] = endRoll[i]
    }
  } else {
    frontRoll = Array.from({ length: props.showNums - props.fixNums }, () => {
      const index = rollStartIndex
      rollStartIndex = rollStartIndex + 1 === rollList.value.length ? 0 : rollStartIndex + 1
      return rollList.value[index]
    })
  }
  endRoll = Array.from({ length: props.showNums - props.fixNums }, () => {
    const index = rollStartIndex
    rollStartIndex = rollStartIndex + 1 === rollList.value.length ? 0 : rollStartIndex + 1
    return rollList.value[index]
  })

  fliperTextRef.value = fliperTextRef.value.map((item, index) => {
    if (index < frontRoll.length) {
      return {
        frontText: { text: frontRoll[index], color: itemStyle.value.color, background: itemStyle.value.background },
        backText: { text: endRoll[index], color: itemStyle.value.color, background: itemStyle.value.background },
      }
    }
    return item
  })
}

const setData = (val: any[]) => {
  const newList = cloneDeep(val)
  componentLoading.value = false
  fixList.value = newList.splice(0, props.fixNums)
  rollList.value = newList
  if (!roundTimer) {
    fliperTextRef.value = Array.from({ length: props.showNums - props.fixNums }, () => ({
      frontText: { text: "-", color: itemStyle.value.color, background: itemStyle.value.background },
      backText: undefined,
    }))
    change()
    roundTimer = setInterval(() => {
      change()
    }, props.duration)
  }
}

onUnmounted(() => {
  if (roundTimer) clearInterval(roundTimer)
})

defineExpose({ loading, setData })
</script>

<style lang="scss" scoped>
.data-row {
  gap: 5px;
}
.data-item {
  display: inline-block;
  position: relative;
  border-radius: 5px;
  background: #fff;
  color: #000000;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);
  text-align: center;
  font-family: "Helvetica Neue";
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 0 0 0 5px;
  &:first-child {
    margin-left: 0;
  }
}
</style>
